/**
 * sqlite3mt snapshot persistence and sync retry helpers
 *
 * Mechanical extraction from §96 §19.1–§19.2.
 *
 * This module owns the reusable control-plane pieces for:
 * - durable snapshot capture from live Control Worker state
 * - serial snapshot persistence with journal truncation fencing
 * - coalesced background snapshot persistence requests
 * - transient-vs-fatal sync error routing and retry scheduling
 */

import { SdkError } from '../errors'
import { EngineException } from '../types'
import type { PendingBaseAdvance } from './bootstrap'
import type { DurableLocalSnapshot } from './durable'
import type {
    ControlWorkerState,
    Delta,
    FrameMax,
    PageHash,
    Pgno,
} from './types'

export type SyncFlow = 'push' | 'reconcile'
export type SyncFatalReason = 'PUSH_FATAL' | 'RECONCILE_FATAL'
export type HandleSyncErrorResult = 'RETRY_SCHEDULED' | 'FATAL'

export interface SnapshotPersistenceState {
    pendingBaseAdvance: PendingBaseAdvance | null
    snapshotPersistTail: Promise<void>
    snapshotPersistBusy: boolean
    snapshotPersistWanted: boolean
}

export interface SnapshotPersistenceRuntime {
    syncFatalError: unknown | null
}

export interface SnapshotPersistenceDurableStore {
    writeSnapshot(snapshot: DurableLocalSnapshot): Promise<void> | void
    truncateJournalThrough(throughSeq: bigint): Promise<void> | void
}

export interface CaptureDurableSnapshotOptions {
    requiredBlobHashes?: Iterable<PageHash>
    requiredManifestNodeHashes?: Iterable<string>
}

export interface PersistSnapshotSerialArgs {
    state: Pick<SnapshotPersistenceState, 'snapshotPersistTail'>
    runtime: SnapshotPersistenceRuntime
    durable: SnapshotPersistenceDurableStore
    snapshot: DurableLocalSnapshot
    truncateJournal: boolean
}

export interface RequestPersistLocalSnapshotArgs {
    state: SnapshotPersistenceState
    runtime: SnapshotPersistenceRuntime
    durable: SnapshotPersistenceDurableStore
    captureDurableSnapshot(): DurableLocalSnapshot
    onPersistError?(error: unknown): void
}

export interface SyncRetryState {
    syncRetryScheduled: boolean
}

export type TimeoutScheduler = (callback: () => void, delayMs: number) => unknown

export interface HandleTransientOrFatalSyncErrorArgs {
    flow: SyncFlow
    error: unknown
    delayMs: number
    state: SyncRetryState
    maybeStartPushOrImport(): void
    enterFatalRuntime(error: unknown, reason: SyncFatalReason): void
    setTimeoutFn?: TimeoutScheduler
    isTransientSyncErrorFn?: (error: unknown) => boolean
    classifyFatalSyncErrorFn?: (flow: SyncFlow, error: unknown) => unknown
}

/**
 * Capture a durable snapshot from current live Control Worker state.
 *
 * The snapshot carries the page hashes needed to replay the sparse state
 * directly from durable storage, plus the manifest roots that define the two
 * concrete views stored in the snapshot.
 */
export function captureDurableSnapshot(
    state: ControlWorkerState,
    options: CaptureDurableSnapshotOptions = {}
): DurableLocalSnapshot {
    const requiredBlobHashes = new Set<PageHash>()
    addBlobHashes(requiredBlobHashes, state.dbOverlay)
    addBlobHashes(requiredBlobHashes, state.workspaceOverlay)
    for (const delta of state.pendingDbDeltas.values()) {
        addBlobHashes(requiredBlobHashes, delta.pageHashes)
    }
    if (options.requiredBlobHashes != null) {
        for (const hash of options.requiredBlobHashes) {
            requiredBlobHashes.add(hash)
        }
    }

    const requiredManifestNodeHashes = new Set<string>([
        state.dbBaseRoot.meta.manifestRootId,
        state.workspaceBaseHead.root.meta.manifestRootId,
    ])
    requiredManifestNodeHashes.delete('')
    if (options.requiredManifestNodeHashes != null) {
        for (const hash of options.requiredManifestNodeHashes) {
            if (hash) {
                requiredManifestNodeHashes.add(hash)
            }
        }
    }

    return {
        schemaVersion: 3,
        dbBaseRoot: cloneRoot(state.dbBaseRoot),
        dbOverlay: new Map(state.dbOverlay),
        dbPageCount: state.dbPageCount,
        workspaceBaseHead: clonePublicHead(state.workspaceBaseHead),
        workspaceOverlay: new Map(state.workspaceOverlay),
        workspacePageCount: state.workspacePageCount,
        pendingDbDeltas: clonePendingDbDeltas(state.pendingDbDeltas),
        currentWalSalt0: state.currentWalSalt0,
        currentWalSalt1: state.currentWalSalt1,
        integratedFrameMax: state.integratedFrameMax,
        lastWalSnapshot: state.lastWalSnapshot == null
            ? null
            : { ...state.lastWalSnapshot },
        localCommitSeq: state.localCommitSeq,
        requiredBlobHashes,
        requiredManifestNodeHashes,
    }
}

/**
 * Serialise snapshot writes behind snapshotPersistTail.
 *
 * Prior write failures must not permanently poison the queue. Journal truncation
 * is only allowed after a successful snapshot write and only while the runtime
 * is still non-fatal.
 */
export function persistSnapshotSerial({
    state,
    runtime,
    durable,
    snapshot,
    truncateJournal,
}: PersistSnapshotSerialArgs): Promise<void> {
    const prev = state.snapshotPersistTail.catch(() => {})

    state.snapshotPersistTail = prev.then(async () => {
        await durable.writeSnapshot(snapshot)
        if (truncateJournal && runtime.syncFatalError == null) {
            await durable.truncateJournalThrough(snapshot.localCommitSeq)
        }
    })

    return state.snapshotPersistTail
}

/**
 * Queue a background local snapshot persist request.
 *
 * Concurrent requests are coalesced through snapshotPersistWanted. The caller
 * may ignore the returned promise; a defensive catch prevents the detached task
 * from surfacing as an unhandled rejection.
 */
export function requestPersistLocalSnapshot({
    state,
    runtime,
    durable,
    captureDurableSnapshot: captureSnapshot,
    onPersistError,
}: RequestPersistLocalSnapshotArgs): Promise<void> | undefined {
    if (runtime.syncFatalError != null) {
        return undefined
    }

    if (state.pendingBaseAdvance != null) {
        state.snapshotPersistWanted = true
        return undefined
    }

    if (state.snapshotPersistBusy) {
        state.snapshotPersistWanted = true
        return undefined
    }

    state.snapshotPersistBusy = true
    state.snapshotPersistWanted = false

    const task = (async () => {
        try {
            do {
                state.snapshotPersistWanted = false
                const snapshot = captureSnapshot()
                await persistSnapshotSerial({
                    state,
                    runtime,
                    durable,
                    snapshot,
                    truncateJournal: true,
                })
            } while (
                state.snapshotPersistWanted
                && runtime.syncFatalError == null
                && state.pendingBaseAdvance == null
            )
        } finally {
            state.snapshotPersistBusy = false
            if (
                state.snapshotPersistWanted
                && runtime.syncFatalError == null
                && state.pendingBaseAdvance == null
            ) {
                requestPersistLocalSnapshot({
                    state,
                    runtime,
                    durable,
                    captureDurableSnapshot: captureSnapshot,
                    onPersistError,
                })
            }
        }
    })()

    void task.catch(error => {
        onPersistError?.(error)
    })

    return task
}

/**
 * Narrow transient classifier for background sync failures.
 *
 * Retryable SDK/engine errors stay in the sync loop. Everything else is routed
 * through fatal handling unless the caller overrides the classifier.
 */
export function isTransientSyncError(error: unknown): boolean {
    if (error instanceof SdkError) {
        return error.retryable
    }
    if (error instanceof EngineException) {
        return error.error.retryable
    }
    if (error instanceof TypeError || hasAbortName(error)) {
        return true
    }
    if (hasRetryableFlag(error)) {
        return error.retryable
    }
    if (hasNestedRetryableFlag(error)) {
        return error.error.retryable
    }
    return false
}

function hasAbortName(error: unknown): boolean {
    return error instanceof DOMException
        ? error.name === 'AbortError'
        : error instanceof Error && error.name === 'AbortError'
}

function hasAuthErrorMessage(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false
    }

    return error.message.startsWith('Auth challenge failed:')
        || error.message.startsWith('Auth session failed:')
        || error.message.startsWith('Auth bootstrap failed:')
        || error.message.startsWith('Invite redeem failed:')
}

/**
 * Background dataplane/auth failures must remain non-fatal to the sqlite
 * runtime so local writes can continue while sync is degraded.
 */
export function isNonFatalSyncError(error: unknown): boolean {
    if (isTransientSyncError(error)) {
        return true
    }
    if (error instanceof SdkError || error instanceof EngineException) {
        return true
    }
    if (error instanceof TypeError || hasAbortName(error)) {
        return true
    }
    return hasAuthErrorMessage(error)
}

export function normalizeSyncError(error: unknown): Error {
    if (error instanceof Error) {
        return error
    }
    return new Error(typeof error === 'string' ? error : String(error))
}

/**
 * Default fatal classifier for sync flows.
 *
 * The spec defines the routing shape but does not impose extra wrapping, so the
 * original error object is preserved unless it is nullish.
 */
export function classifyFatalSyncError(flow: SyncFlow, error: unknown): unknown {
    if (error != null) {
        return error
    }
    return new Error(`${flow} sync fatal`)
}

/**
 * Schedule one retry timer for the sync engine.
 *
 * Re-entrant requests are collapsed while a retry timer is already pending.
 */
export function scheduleSyncRetry(
    state: SyncRetryState,
    delayMs: number,
    maybeStartPushOrImport: () => void,
    setTimeoutFn: TimeoutScheduler = (callback, timeoutMs) => globalThis.setTimeout(callback, timeoutMs)
): boolean {
    if (state.syncRetryScheduled) {
        return false
    }

    state.syncRetryScheduled = true
    setTimeoutFn(() => {
        state.syncRetryScheduled = false
        maybeStartPushOrImport()
    }, delayMs)
    return true
}

/**
 * Route a sync failure either into retry scheduling or fatal runtime entry.
 */
export function handleTransientOrFatalSyncError({
    flow,
    error,
    delayMs,
    state,
    maybeStartPushOrImport,
    enterFatalRuntime,
    setTimeoutFn,
    isTransientSyncErrorFn = isTransientSyncError,
    classifyFatalSyncErrorFn = classifyFatalSyncError,
}: HandleTransientOrFatalSyncErrorArgs): HandleSyncErrorResult {
    if (isTransientSyncErrorFn(error)) {
        scheduleSyncRetry(
            state,
            delayMs,
            maybeStartPushOrImport,
            setTimeoutFn
        )
        return 'RETRY_SCHEDULED'
    }

    enterFatalRuntime(
        classifyFatalSyncErrorFn(flow, error),
        syncFatalReasonForFlow(flow)
    )
    return 'FATAL'
}

function syncFatalReasonForFlow(flow: SyncFlow): SyncFatalReason {
    return flow === 'push' ? 'PUSH_FATAL' : 'RECONCILE_FATAL'
}

function addBlobHashes(
    target: Set<PageHash>,
    hashes: ReadonlyMap<Pgno, PageHash>
): void {
    for (const hash of hashes.values()) {
        if (hash) {
            target.add(hash)
        }
    }
}

function clonePendingDbDeltas(
    pendingDbDeltas: ReadonlyMap<FrameMax, Delta>
): Map<FrameMax, Delta> {
    return new Map(
        [...pendingDbDeltas].map(([frameMax, delta]) => [frameMax, cloneDelta(delta)])
    )
}

function cloneDelta(delta: Delta): Delta {
    return {
        pageHashes: new Map(delta.pageHashes),
        postPageCount: delta.postPageCount,
    }
}

function clonePublicHead(head: ControlWorkerState['workspaceBaseHead']): ControlWorkerState['workspaceBaseHead'] {
    return {
        root: cloneRoot(head.root),
        version: head.version,
    }
}

function cloneRoot(root: ControlWorkerState['dbBaseRoot']): ControlWorkerState['dbBaseRoot'] {
    return {
        rootId: root.rootId,
        meta: {
            manifestRootId: root.meta.manifestRootId,
            pageCount: root.meta.pageCount,
            parentPublicRootId: root.meta.parentPublicRootId,
        },
    }
}

function hasRetryableFlag(error: unknown): error is { retryable: boolean } {
    return isRecord(error) && typeof error.retryable === 'boolean'
}

function hasNestedRetryableFlag(error: unknown): error is { error: { retryable: boolean } } {
    return isRecord(error)
        && isRecord(error.error)
        && typeof error.error.retryable === 'boolean'
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value != null
}
